/** Login screen mockup — full-bleed luxury */
import React from 'react';
import { Wrench, ArrowRight, ShieldCheck, Sparkles, Building2 } from 'lucide-react';
import { C, fontHead, primaryBtn } from '../_tokens';

export default function ScreenLogin() {
  return (
    <div style={{
      minHeight: '100vh', display: 'grid', gridTemplateColumns: '1.2fr 1fr',
      background: C.bg, color: C.text, position: 'relative', overflow: 'hidden',
    }}>
      {/* LEFT: Brand hero */}
      <div style={{
        background: `
          radial-gradient(ellipse 70% 50% at 30% 20%, rgba(212,175,55,0.18), transparent 50%),
          radial-gradient(ellipse 60% 60% at 80% 100%, rgba(226,125,96,0.12), transparent 50%),
          linear-gradient(180deg, ${C.bgDeep}, ${C.bg})`,
        padding: '60px 70px', display: 'flex', flexDirection: 'column',
        justifyContent: 'space-between', position: 'relative',
      }}>
        {/* Subtle grid texture */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px),
                            linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
          backgroundSize: '64px 64px', pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
              display: 'grid', placeItems: 'center', boxShadow: `0 0 32px ${C.goldGlow}`,
            }}><Wrench size={22} color={C.bg} strokeWidth={2.5} /></div>
            <div>
              <div style={{ fontFamily: fontHead, fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' }}>
                HostFix<span style={{ color: C.gold }}>.</span>
              </div>
              <div style={{ fontSize: 10.5, color: C.textSec, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                by PTK Solutions
              </div>
            </div>
          </div>
        </div>

        <div style={{ position: 'relative', maxWidth: 560 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <Sparkles size={14} color={C.gold} />
            <span style={{
              fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
              color: C.gold, fontWeight: 600,
            }}>Operations · Hospitality Group Edition</span>
          </div>
          <h1 style={{
            fontFamily: fontHead, fontSize: 64, fontWeight: 500, letterSpacing: '-0.035em',
            lineHeight: 1.02, margin: 0,
          }}>
            La centrale operativa<br />
            del tuo gruppo<br />
            <span style={{ color: C.gold, fontStyle: 'italic' }}>alberghiero.</span>
          </h1>
          <p style={{
            color: C.textSec, fontSize: 16, lineHeight: 1.55, marginTop: 22, maxWidth: 480,
          }}>
            Manutenzioni, ticket, consumi, alert predittivi. Tutto in un&apos;unica console
            disegnata per chi gestisce hotel di livello.
          </p>
          <div style={{ display: 'flex', gap: 28, marginTop: 36, color: C.textSec, fontSize: 13 }}>
            <Stat n="6" label="Hotel attivi" />
            <Stat n="2.847" label="Ticket gestiti" />
            <Stat n="78.4" label="Indice efficienza" accent={C.gold} />
          </div>
        </div>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, color: C.textDim, fontSize: 12 }}>
          <ShieldCheck size={13} /> Crittografia end-to-end · GDPR compliant · ISO/IEC 27001
        </div>
      </div>

      {/* RIGHT: Login form */}
      <div style={{
        background: C.surf, padding: '60px 70px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        borderLeft: `1px solid ${C.border}`,
      }}>
        <div style={{ maxWidth: 380, width: '100%', alignSelf: 'center' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
                        color: C.textSec, fontWeight: 600, marginBottom: 10 }}>Bentornato</div>
          <h2 style={{ fontFamily: fontHead, fontSize: 36, fontWeight: 600, margin: 0,
                       letterSpacing: '-0.025em', lineHeight: 1.1 }}>
            Accedi alla console
          </h2>
          <p style={{ color: C.textSec, fontSize: 14, marginTop: 10, marginBottom: 36 }}>
            Inserisci le credenziali aziendali fornite dal manager.
          </p>

          <Field label="Indirizzo email" placeholder="nome@investhospitality.com" />
          <div style={{ height: 18 }} />
          <Field label="Password" placeholder="••••••••••••" type="password" rightLink="Hai dimenticato?" />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22 }}>
            <span style={{
              width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${C.gold}`,
              background: `${C.gold}33`, display: 'grid', placeItems: 'center',
            }}><div style={{ width: 8, height: 8, borderRadius: 2, background: C.gold }} /></span>
            <span style={{ fontSize: 13, color: C.textSec }}>Resta connesso su questo dispositivo</span>
          </div>

          <button style={{
            ...primaryBtn, width: '100%', padding: '14px 18px', fontSize: 14,
            marginTop: 28, justifyContent: 'center',
          }}>
            Accedi <ArrowRight size={15} />
          </button>

          <div style={{ marginTop: 30, padding: 14, background: 'rgba(212,175,55,0.06)',
                        borderRadius: 10, border: `1px solid ${C.gold}33`,
                        display: 'flex', alignItems: 'center', gap: 10 }}>
            <Building2 size={14} color={C.gold} />
            <span style={{ fontSize: 11.5, color: C.textSec }}>
              <b style={{ color: C.gold }}>Single Sign-On</b> aziendale disponibile
            </span>
          </div>

          <div style={{ marginTop: 40, fontSize: 11, color: C.textDim, textAlign: 'center', letterSpacing: '0.05em' }}>
            v.3.0 · © 2026 PTK Solutions per Invest Hospitality
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ n, label, accent }) => (
  <div>
    <div style={{
      fontFamily: fontHead, fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em',
      color: accent || C.text, lineHeight: 1,
    }}>{n}</div>
    <div style={{ fontSize: 11, color: C.textSec, marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
  </div>
);

const Field = ({ label, placeholder, type, rightLink }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
                     color: C.textSec, fontWeight: 600 }}>{label}</span>
      {rightLink && <a style={{ fontSize: 11.5, color: C.gold, textDecoration: 'none' }}>{rightLink}</a>}
    </div>
    <input
      type={type || 'text'} placeholder={placeholder}
      style={{
        width: '100%', padding: '14px 16px',
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
        color: C.text, fontSize: 14, fontFamily: 'inherit', outline: 'none',
      }}
    />
  </div>
);
